import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API as API_IMPORTED } from '@/utils/api';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Loader2, Edit2, Trash2, Eye, EyeOff, ExternalLink,
  Share2, Copy, MoreVertical, Home, Dog, Users, Heart,
  Calendar, MapPin, CheckCircle2, XCircle, Pause, Play,
  ChevronRight, Sparkles, AlertTriangle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ENTITY_CONFIG = {
  kennel: {
    label: 'Hodowla',
    icon: Home,
    color: 'violet',
    editPath: '/panel/profil',
    publicPath: '/hodowla'
  },
  litter: {
    label: 'Miot',
    icon: Heart,
    color: 'pink',
    editPath: '/wizard/miot',
    publicPath: '/miot'
  },
  stud: {
    label: 'Reproduktor',
    icon: Dog,
    color: 'blue',
    editPath: '/wizard/reproduktor',
    publicPath: '/reproduktor'
  },
  dog: {
    label: 'Pies',
    icon: Dog,
    color: 'amber',
    editPath: '/panel/pies',
    editSuffix: '/edytuj',
    publicPath: '/pies'
  }
};

export default function MyProjects() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [filter, setFilter] = useState('all'); // all, active, sleeping

  // Dynamic API URL for production
  const API = useMemo(() => {
    return API_IMPORTED;
  }, []);

  useEffect(() => {
    if (API) fetchProjects();
  }, [token, API]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
        setStats(data.stats || null);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      toast.error('Błąd ładowania projektów');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSleep = async (project) => {
    setActionLoading(prev => ({ ...prev, [project.id]: true }));
    try {
      const res = await fetch(`${API}/projects/${project.type}/${project.id}/toggle-sleep`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.is_sleeping ? 'Strona uśpiona' : 'Strona aktywowana');
        fetchProjects();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Błąd operacji');
      }
    } catch (err) {
      toast.error('Błąd sieci');
    } finally {
      setActionLoading(prev => ({ ...prev, [project.id]: false }));
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć "${project.name}"? Ta operacja jest nieodwracalna.`)) {
      return;
    }

    setActionLoading(prev => ({ ...prev, [project.id]: true }));
    try {
      const res = await fetch(`${API}/projects/${project.type}/${project.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Projekt usunięty');
        fetchProjects();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Błąd usuwania');
      }
    } catch (err) {
      toast.error('Błąd sieci');
    } finally {
      setActionLoading(prev => ({ ...prev, [project.id]: false }));
    }
  };

  const handleCopyLink = (project) => {
    const config = ENTITY_CONFIG[project.type];
    const url = `${window.location.origin}${config.publicPath}/${project.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link skopiowany!');
  };

  const filteredProjects = projects.filter(p => {
    if (filter === 'active') return !p.is_sleeping;
    if (filter === 'sleeping') return p.is_sleeping;
    return true;
  });

  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" data-testid="loading-spinner" />
        </div>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="my-projects-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#F47A3B]" />
              Moje projekty
            </h1>
            <p className="text-zinc-500 mt-1">
              Zarządzaj swoimi stronami i ofertami
            </p>
          </div>

          <div className="flex gap-2">
            <Link to="/panel/profil">
              <Button className="bg-[#F47A3B] hover:bg-[#E06A2B]">
                <Plus className="w-4 h-4 mr-2" />
                Nowy projekt
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-zinc-200">
              <div className="text-2xl font-bold text-zinc-900">{stats.total || 0}</div>
              <div className="text-sm text-zinc-500">Wszystkich</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-zinc-200">
              <div className="text-2xl font-bold text-emerald-600">{stats.active || 0}</div>
              <div className="text-sm text-zinc-500">Aktywnych</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-zinc-200">
              <div className="text-2xl font-bold text-amber-600">{stats.sleeping || 0}</div>
              <div className="text-sm text-zinc-500">Uśpionych</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-zinc-200">
              <div className="text-2xl font-bold text-orange-600">{stats.leads || 0}</div>
              <div className="text-sm text-zinc-500">Leadów</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2">
          {[
            { id: 'all', label: 'Wszystkie' },
            { id: 'active', label: 'Aktywne' },
            { id: 'sleeping', label: 'Uśpione' }
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === f.id
                  ? 'bg-[#F47A3B] text-white'
                  : 'bg-white text-zinc-600 border border-zinc-200 hover:border-zinc-300'
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Projects List */}
        {filteredProjects.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-zinc-200">
            <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-zinc-900 mb-2">
              {filter === 'all' ? 'Brak projektów' : filter === 'active' ? 'Brak aktywnych projektów' : 'Brak uśpionych projektów'}
            </h2>
            <p className="text-zinc-500 mb-6">
              {filter === 'all' ? 'Utwórz swoją pierwszą stronę hodowli lub ofertę' : 'Zmień filtr lub dodaj nowy projekt'}
            </p>
            {filter === 'all' && (
              <Link to="/panel/profil">
                <Button className="bg-[#F47A3B] hover:bg-[#E06A2B]">
                  <Plus className="w-4 h-4 mr-2" />
                  Utwórz pierwszy projekt
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={`${project.type}-${project.id}`}
                project={project}
                onEdit={() => {
                  const config = ENTITY_CONFIG[project.type];
                  const editUrl = config.editSuffix 
                    ? `${config.editPath}/${project.id}${config.editSuffix}`
                    : `${config.editPath}/${project.id}`;
                  navigate(editUrl);
                }}
                onDelete={() => handleDelete(project)}
                onToggleSleep={() => handleToggleSleep(project)}
                onCopyLink={() => handleCopyLink(project)}
                onView={() => window.open(`${ENTITY_CONFIG[project.type].publicPath}/${project.slug}`, '_blank')}
                actionLoading={actionLoading[project.id]}
              />
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Wskazówki
          </h3>
          <ul className="text-sm text-amber-700 space-y-1">
            <li>• <strong>Uśpione strony</strong> są niedostępne publicznie, ale zachowują wszystkie dane</li>
            <li>• <strong>Usuniętych stron</strong> nie można przywrócić - upewnij się przed usunięciem</li>
            <li>• Możesz mieć wiele aktywnych stron jednocześnie</li>
          </ul>
        </div>
      </div>
    </PanelLayout>
  );
}

function ProjectCard({ project, onEdit, onDelete, onToggleSleep, onCopyLink, onView, actionLoading }) {
  const config = ENTITY_CONFIG[project.type] || ENTITY_CONFIG.kennel;
  const Icon = config.icon;
  const isSleeping = project.is_sleeping;

  const colorClasses = {
    violet: 'bg-orange-50 text-orange-600 border-orange-200',
    pink: 'bg-pink-50 text-pink-600 border-pink-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border p-4 transition-all hover:shadow-md ${
        isSleeping ? 'border-zinc-200 opacity-70' : 'border-zinc-200'
      }`}
      data-testid={`project-card-${project.id}`}
    >
      <div className="flex items-center gap-4">
        {/* Icon & Type */}
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center border ${colorClasses[config.color]}`}>
          <Icon className="w-7 h-7" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-zinc-900 font-semibold truncate">{project.name}</h3>
            <Badge variant="outline" className={`text-xs ${colorClasses[config.color]}`}>
              {config.label}
            </Badge>
            {isSleeping && (
              <Badge variant="outline" className="text-xs bg-zinc-100 text-zinc-500 border-zinc-300">
                <EyeOff className="w-3 h-3 mr-1" />
                Uśpiona
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-4 text-sm text-zinc-500">
            {project.city && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {project.city}
              </span>
            )}
            {project.created_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(project.created_at).toLocaleDateString('pl-PL')}
              </span>
            )}
            {project.leads_count > 0 && (
              <span className="flex items-center gap-1 text-emerald-600">
                <Users className="w-3 h-3" />
                {project.leads_count} leadów
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onView}
            className="border-zinc-200 text-zinc-600 hover:text-zinc-900"
            title="Podgląd"
          >
            <Eye className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onCopyLink}
            className="border-zinc-200 text-zinc-600 hover:text-zinc-900"
            title="Kopiuj link"
          >
            <Copy className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleSleep}
            disabled={actionLoading}
            className={`border-zinc-200 ${isSleeping ? 'text-emerald-600 hover:text-emerald-700' : 'text-amber-600 hover:text-amber-700'}`}
            title={isSleeping ? 'Aktywuj' : 'Uśpij'}
          >
            {actionLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isSleeping ? (
              <Play className="w-4 h-4" />
            ) : (
              <Pause className="w-4 h-4" />
            )}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            className="border-zinc-200 text-zinc-600 hover:text-zinc-900"
            title="Edytuj"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            disabled={actionLoading}
            className="border-zinc-200 text-red-500 hover:text-red-600 hover:border-red-200"
            title="Usuń"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
